import { useWorldMarker } from 'hooks/marker/useWorldMarker';
import { ICoordinates } from 'libs/types/map/mapTypes';
import { EToastType } from 'libs/types/site/IClient';
import { getCoordinatesBoundaries, parseStravaGPX } from 'libs/utils/gpx/gpxUtils';
import { userMessage } from 'libs/utils/logic/ui/uiLogic';
import { EWorldAction } from 'libs/utils/world/worldTypes';
import React, { useContext } from 'react';
import {
    IDrawWorld,
    ILinkWorld,
    IPinWorld,
    IPolygonWorld,
    IRouteWorld,
    ITextWorld,
    IWorldMarker,
    IYoutubeWorld,
    ORIGIN_ZOOM,
    TEXT_BORDER_RADIUS,
    getInScale
} from 'react-give-me-map';
import { IPinWorldMarker, IReactWorld } from 'react-give-me-map/dist/utils/world/worldTypes';
import { CtxEdit } from 'session/ctxEdit';
import { EWorldColor } from 'static/config/worldVariables';
import { Localization } from 'static/localization/localization';

export const useCreateWorldMarker = (): IUseCreateWorldMarker => {
    const worldMarker = useWorldMarker();
    const ctxEdit = useContext(CtxEdit);

    function createTextMarker(lat: number, lng: number, zoom: number) {
        const id = Math.random().toString();
        const textMarker = {
            id: id,
            lat,
            lng,
            selectable: true,
            visible: true,
            elementType: 'text' as const,
            elementData: {
                text: '',
                width: 0,
                fill: true,
                color: ctxEdit.markerDefault.color,
                borderRadiusPx: TEXT_BORDER_RADIUS
            } as ITextWorld,
            scale: getInScale(1, zoom, ORIGIN_ZOOM),
            scalable: true,
            rotate: 0,
            isExternalPoint: false,
            ref: React.createRef<HTMLDivElement>(),
            order: worldMarker.findHighestOrder() + 1
        };
        ctxEdit.addWorldMarker(textMarker);
        ctxEdit.setSelectedMarkers([id]);
        ctxEdit.setWorldAction(EWorldAction.SELECT);
    }

    function createLinkMarker(lat: number, lng: number, zoom: number) {
        const id = Math.random().toString();
        const linkMarker = {
            id: id,
            lat,
            lng,
            selectable: true,
            visible: true,
            elementType: 'link' as const,
            elementData: {
                text: '',
                link: '',
                width: 0,
                fill: true,
                color: EWorldColor.WHITE,
                borderRadiusPx: TEXT_BORDER_RADIUS
            } as ILinkWorld,
            scale: getInScale(1, zoom, ORIGIN_ZOOM),
            scalable: true,
            rotate: 0,
            isExternalPoint: false,
            ref: React.createRef<HTMLDivElement>(),
            order: worldMarker.findHighestOrder() + 1
        };
        ctxEdit.addWorldMarker(linkMarker);
        ctxEdit.setSelectedMarkers([id]);
        ctxEdit.setWorldAction(EWorldAction.SELECT);
    }

    function createReactMarker(lat: number, lng: number, zoom: number) {
        const id = Math.random().toString();
        const reactMarker = {
            id: id,
            lat,
            lng,
            selectable: true,
            visible: true,
            elementType: 'react' as const,
            elementData: {
                componentTypeId: ctxEdit.markerDefault.reactCustomMarkerType,
                componentData: {}
            } as IReactWorld,
            scale: getInScale(1, zoom, ORIGIN_ZOOM),
            scalable: true,
            rotate: 0,
            isExternalPoint: false,
            ref: React.createRef<HTMLDivElement>(),
            order: worldMarker.findHighestOrder() + 1
        };
        ctxEdit.addWorldMarker(reactMarker);
        ctxEdit.setSelectedMarkers([id]);
        ctxEdit.setWorldAction(EWorldAction.SELECT);
    }

    function createImageMarker(lat: number, lng: number, zoom: number) {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept =
            'image/x-png,image/png,image/gif,image/jpeg,image/jpg,image/heic,image/heif,image/svg+xml,.heic;capture=camera';
        input.multiple = true;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        input.onchange = (e: any) =>
            ctxEdit.setFilesToUpload([
                ...ctxEdit.filesToUpload,
                {
                    type: 'image',
                    lat,
                    lng,
                    zoom: zoom,
                    fileList: e.target.files
                }
            ]);
        ctxEdit.setWorldAction(EWorldAction.SELECT);
        input.click();
    }

    function createRouteMarker(url: string) {
        const request = new XMLHttpRequest();

        request.addEventListener(
            'load',
            function (evt) {
                const content = evt?.target?.['response'];
                if (content) {
                    const coordinates = parseStravaGPX(content);
                    if (!coordinates || coordinates.length === 0) {
                        userMessage(EToastType.ERROR, Localization.UNKNOWN_GPX_FILE);
                        return;
                    }
                    const boundaries = getCoordinatesBoundaries(coordinates);
                    const routeMarker: IWorldMarker = {
                        id: Math.random().toString(),
                        lat: boundaries.center.lat,
                        lng: boundaries.center.lng,
                        selectable: true,
                        visible: true,
                        elementType: 'route' as const,
                        elementData: {
                            coordinates: coordinates,
                            lineColor: EWorldColor.RED
                        } as IRouteWorld,
                        scalable: false,
                        isExternalPoint: false,
                        ref: React.createRef(),
                        order: worldMarker.findHighestOrder() + 1
                    };
                    ctxEdit.addWorldMarker(routeMarker);
                    ctxEdit.mapRef?.current.flyTo({
                        center: [coordinates[0]?.lng, coordinates[0]?.lat],
                        zoom:
                            ctxEdit.mapRef?.current.getZoom() > 13
                                ? ctxEdit.mapRef?.current.getZoom()
                                : 13
                    });
                }
            },
            false
        );

        request.open('GET', url, true), request.send();
        ctxEdit.setWorldAction(EWorldAction.SELECT);
    }

    function createDrawMarker(
        lat: number,
        lng: number,
        width: number,
        height: number,
        path: string,
        type: string,
        opacity: number,
        strokeWidth: number,
        color: string,
        fill: boolean,
        zoom: number
    ) {
        const drawMarker: IWorldMarker = {
            id: Math.random().toString(),
            lat: lat,
            lng: lng,
            selectable: true,
            visible: true,
            elementType: 'draw' as const,
            elementData: {
                width,
                height,
                path,
                type,
                opacity,
                strokeWidth,
                color,
                fill
            } as IDrawWorld,
            ref: React.createRef(),
            scale: getInScale(1, zoom, ORIGIN_ZOOM),
            scalable: true,
            isExternalPoint: false,
            order: worldMarker.findHighestOrder() + 1,
            rotate: 0
        };
        console.log('Creating', drawMarker);
        ctxEdit.addWorldMarker(drawMarker);
    }

    function createPinMarker(
        lat: number,
        lng: number,
        data?: { label?: string; gptContextId?: string }
    ) {
        const id = Math.random().toString();
        const pinMarker: IPinWorldMarker = {
            id: id,
            lat,
            lng,
            selectable: true,
            visible: true,
            gptContextId: data?.gptContextId,
            elementType: 'pin' as const,
            elementData: {
                label: data?.label ?? '',
                img: '',
                emoji: '',
                dotColor: ctxEdit.markerDefault.color,
                icon: null
            } as IPinWorld,
            ref: React.createRef<HTMLDivElement>(),
            scalable: false,
            scale: 1,
            isExternalPoint: false,
            rotate: 0,
            order: worldMarker.findHighestOrder() + 1,
            isGroupable: false
        };
        ctxEdit.addWorldMarker(pinMarker);
        ctxEdit.setSelectedMarkers([id]);
        ctxEdit.setWorldAction(EWorldAction.SELECT);
    }

    function createPolygonMarker(
        lat: number,
        lng: number,
        path: string,
        coordinates: ICoordinates[],
        width: number,
        height: number,
        color: EWorldColor | string,
        fill: boolean,
        zoom: number
    ) {
        const polygonMarker: IWorldMarker = {
            id: Math.random().toString(),
            lat: lat,
            lng: lng,
            selectable: true,
            visible: true,
            elementType: 'polygon' as const,
            elementData: {
                path,
                coordinates,
                width,
                height,
                color,
                fill
            } as IPolygonWorld,
            scale: getInScale(1, zoom, ORIGIN_ZOOM),
            scalable: true,
            order: worldMarker.findHighestOrder() + 1,
            ref: React.createRef(),
            isExternalPoint: false,
            rotate: 0
        };
        console.log('Creating', polygonMarker);
        ctxEdit.addWorldMarker(polygonMarker);
    }

    function createYoutubeMarker(lat: number, lng: number, zoom: number) {
        const id = Math.random().toString();
        const youtubeMarker = {
            id: id,
            lat,
            lng,
            selectable: true,
            visible: true,
            elementType: 'youtube' as const,
            elementData: {
                video: ''
            } as IYoutubeWorld,
            scale: getInScale(1, zoom, ORIGIN_ZOOM),
            scalable: true,
            ref: React.createRef<HTMLDivElement>(),
            rotate: 0,
            isExternalPoint: false,
            order: worldMarker.findHighestOrder() + 1
        };
        ctxEdit.addWorldMarker(youtubeMarker);
        ctxEdit.setSelectedMarkers([id]);
        ctxEdit.setWorldAction(EWorldAction.SELECT);
    }

    return {
        createTextMarker,
        createLinkMarker,
        createImageMarker,
        createRouteMarker,
        createDrawMarker,
        createPinMarker,
        createPolygonMarker,
        createYoutubeMarker,
        createReactMarker
    };
};

export interface IUseCreateWorldMarker {
    createTextMarker: (lat: number, lng: number, zoom: number) => void;
    createLinkMarker: (lat: number, lng: number, zoom: number) => void;
    createImageMarker: (lat: number, lng: number, zoom: number) => void;
    createRouteMarker: (url: string) => void;
    createDrawMarker: (
        lat: number,
        lng: number,
        width: number,
        height: number,
        path: string,
        type: string,
        opacity: number,
        strokeWidth: number,
        color: string,
        fill: boolean,
        zoom: number
    ) => void;
    createPinMarker: (
        lat: number,
        lng: number,
        data?: { label?: string; gptContextId?: string }
    ) => void;
    createPolygonMarker: (
        lat: number,
        lng: number,
        path: string,
        coordinates: ICoordinates[],
        width: number,
        height: number,
        color: EWorldColor | string,
        fill: boolean,
        zoom: number
    ) => void;

    createYoutubeMarker: (lat: number, lng: number, zoom: number) => void;
    createReactMarker: (lat: number, lng: number, zoom: number) => void;
}
